import React from 'react';
import { MapPin, Users, Phone, CreditCard, Car, Accessibility, Clock, Navigation, ChevronRight, Instagram, Camera } from 'lucide-react';
import { isVenueClaimed, isMobilePhone } from './venueHelpers';

const VenueContactGrid = React.memo(({ venue, onDirectionsClick, onPhoneCall, onUnclaimedWhatsAppClick }) => {
  const claimed = isVenueClaimed(venue);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Address Card - Onaysız mekanlarda KVKK gereği tam adres gizlenir, sadece ilçe/il gösterilir */}
      <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group">
        <div className="flex items-start gap-4">
          <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 mb-2">Adres</p>
            {claimed ? (
              <>
                <p className="text-gray-600 text-sm break-words leading-relaxed">{venue.address}</p>
                <p className="text-gray-500 text-sm mt-1">{venue.districtName}, {venue.cityName}</p>
              </>
            ) : (
              <>
                <p className="text-gray-600 text-sm break-words leading-relaxed">{venue.districtName}, {venue.cityName}</p>
                <p className="text-gray-400 text-xs mt-1 italic">Detaylı adres, mekan sahibi doğrulandıktan sonra görüntülenir.</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Capacity Card */}
      {venue.capacity && (venue.capacity.min || venue.capacity.max) && (
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 mb-2">Kapasite</p>
              <p className="text-gray-600 text-sm">
                {venue.capacity.min && venue.capacity.max 
                  ? `${venue.capacity.min} - ${venue.capacity.max} Kişi`
                  : venue.capacity.max ? `${venue.capacity.max} Kişi` : `${venue.capacity.min}+ Kişi`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Phone Card - Onaysız mekanlarda KVKK gereği telefon gizlenir */}
      {venue.phone && claimed && (
        <div 
          className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group cursor-pointer"
          onClick={onPhoneCall}
        >
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 mb-2">Telefon</p>
              <p className="text-green-600 font-semibold text-sm group-hover:underline">{venue.phone}</p>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Card (website alanında instagram varsa) */}
      {venue.website && venue.website.includes('instagram') && (
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Instagram className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 mb-2">Instagram</p>
              <a href={venue.website.startsWith('http') ? venue.website : `https://${venue.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline break-all">
                {venue.website.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\?.*$/, '').replace(/\/$/, '')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Mekan Instagram Kartı (instagramHandle alanından) */}
      {venue.instagramHandle && !venue.website?.includes('instagram') && (
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Instagram className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 mb-2">Instagram</p>
              <a href={`https://www.instagram.com/${venue.instagramHandle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
                @{venue.instagramHandle.replace('@', '')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Fotoğrafçı Instagram Kartı */}
      {venue.photographer?.instagram && (
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-amber-500 to-rose-500 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 mb-1">{venue.photographer?.name || 'Fotografci'}</p>
              <a href={venue.photographer.instagram.startsWith('http') ? venue.photographer.instagram : `https://www.instagram.com/${venue.photographer.instagram.replace('@', '')}`}
                target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline flex items-center gap-1">
                <Instagram className="w-3.5 h-3.5" />
                {venue.photographer.instagram.includes('instagram.com')
                  ? venue.photographer.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\?.*$/, '').replace(/\/$/, '')
                  : `@${venue.photographer.instagram.replace('@', '')}`}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Working Hours Card */}
      {venue.googleOpeningHours && venue.googleOpeningHours.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group sm:col-span-2">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 p-3 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 mb-3">Çalışma Saatleri</p>
              <div className="space-y-1.5">
                {venue.googleOpeningHours.map((hours, index) => (
                  <p key={index} className="text-gray-600 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></span>
                    {hours}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment & Parking Card */}
      {(() => {
        const hasPayment = venue.googlePayment && (venue.googlePayment.acceptsCreditCards || venue.googlePayment.acceptsDebitCards || venue.googlePayment.acceptsCashOnly || venue.googlePayment.acceptsNfc);
        const hasParking = venue.googleParking && (venue.googleParking.freeParkingLot || venue.googleParking.paidParkingLot || venue.googleParking.valetParking || venue.googleParking.streetParking);
        const hasAccessibility = venue.googleAccessibility && (venue.googleAccessibility.wheelchairAccessibleEntrance || venue.googleAccessibility.wheelchairAccessibleRestroom || venue.googleAccessibility.wheelchairAccessibleSeating);
        
        if (!hasPayment && !hasParking && !hasAccessibility) return null;
        
        return (
          <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 group sm:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {hasPayment && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                    <p className="font-bold text-gray-900 text-sm">Ödeme</p>
                  </div>
                  <div className="space-y-1.5">
                    {venue.googlePayment.acceptsCreditCards && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Kredi Kartı</p>}
                    {venue.googlePayment.acceptsDebitCards && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Banka Kartı</p>}
                    {venue.googlePayment.acceptsCashOnly && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Nakit</p>}
                    {venue.googlePayment.acceptsNfc && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Temassız</p>}
                  </div>
                </div>
              )}
              {hasParking && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Car className="w-5 h-5 text-blue-600" />
                    <p className="font-bold text-gray-900 text-sm">Otopark</p>
                  </div>
                  <div className="space-y-1.5">
                    {venue.googleParking.freeParkingLot && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Ücretsiz Otopark</p>}
                    {venue.googleParking.paidParkingLot && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>Ücretli Otopark</p>}
                    {venue.googleParking.valetParking && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Vale Parking</p>}
                    {venue.googleParking.streetParking && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>Sokak Parkı</p>}
                  </div>
                </div>
              )}
              {hasAccessibility && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Accessibility className="w-5 h-5 text-green-600" />
                    <p className="font-bold text-gray-900 text-sm">Erişilebilirlik</p>
                  </div>
                  <div className="space-y-1.5">
                    {venue.googleAccessibility.wheelchairAccessibleEntrance && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Engelli Girişi</p>}
                    {venue.googleAccessibility.wheelchairAccessibleRestroom && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Engelli WC</p>}
                    {venue.googleAccessibility.wheelchairAccessibleSeating && <p className="text-gray-600 text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Engelli Oturma</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
});

VenueContactGrid.displayName = 'VenueContactGrid';
export default VenueContactGrid;
